import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'rad-storefront-auth-v1';

const StorefrontAuthContext = createContext({
  customer: null,
  loading: true,
  token: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  forgotPassword: async () => {},
  resetPassword: async () => {},
  authedFetch: () => {},
});

const loadToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

/**
 * Auth provider for the public storefront customer-facing account flow.
 * Stores the JWT in localStorage and hydrates the current customer from
 * /api/storefront/auth/me on mount.
 */
export const StorefrontAuthProvider = ({ children }) => {
  const [token, setToken] = useState(loadToken);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const authedFetch = useCallback(async (path, options = {}) => {
    const url = path.startsWith('http') ? path : `${API}${path}`;
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios({ url, method: options.method || 'get', data: options.body, headers });
    return res.data;
  }, [token]);

  // Hydrate on mount / whenever token changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setCustomer(null); setLoading(false); return; }
      setLoading(true);
      try {
        const res = await axios.get(`${API}/storefront/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setCustomer(res.data);
      } catch {
        if (!cancelled) {
          // Token expired / invalid
          try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
          setToken(null);
          setCustomer(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const persist = useCallback((nextToken) => {
    try {
      if (nextToken) localStorage.setItem(TOKEN_KEY, nextToken);
      else localStorage.removeItem(TOKEN_KEY);
    } catch { /* ignore quota */ }
    setToken(nextToken);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/storefront/auth/login`, { email, password });
    persist(res.data.access_token);
    setCustomer(res.data.customer);
    return res.data.customer;
  }, [persist]);

  const register = useCallback(async (email, password, displayName) => {
    const res = await axios.post(`${API}/storefront/auth/register`, {
      email, password, display_name: displayName || null,
    });
    persist(res.data.access_token);
    setCustomer(res.data.customer);
    return res.data.customer;
  }, [persist]);

  const logout = useCallback(() => {
    persist(null);
    setCustomer(null);
  }, [persist]);

  const forgotPassword = useCallback(async (email) => {
    await axios.post(`${API}/storefront/auth/forgot`, { email });
  }, []);

  const resetPassword = useCallback(async (resetToken, password) => {
    await axios.post(`${API}/storefront/auth/reset`, { token: resetToken, password });
  }, []);

  const value = useMemo(() => ({
    customer, token, loading, login, register, logout, forgotPassword, resetPassword, authedFetch,
  }), [customer, token, loading, login, register, logout, forgotPassword, resetPassword, authedFetch]);

  return (
    <StorefrontAuthContext.Provider value={value}>{children}</StorefrontAuthContext.Provider>
  );
};

export const useStorefrontAuth = () => useContext(StorefrontAuthContext);
